"use client";

/** Turns account events and client actions into localized trading notifications. */

import {
  ClientNotificationType,
  Market,
  NotificationType,
  UserNotification,
} from "@decibeltrade/sdk";
import { CreateToastArgs, VisibleToast } from "@decibeltrade/web-core/components";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
} from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";
import { v4 as uuidv4 } from "uuid";

import { OrderSide } from "@/app/(main)/trade/order-form/order-form-state/store";
import { toast } from "@/components/toast";
import { getMarketByAddress } from "@/contexts/queries/markets";
import { Logger } from "@/utils/logger";
import { getMarketCurrencies } from "@/utils/market";
import { NumberFormatter } from "@/utils/number-formatter";
import { getCancelledSize, getFilledSize, usesRemovedSizeDelta } from "@/utils/order-event-sizes";

import { useAgentObservedFills } from "./agent-observed-fills";
import { readDexSdk } from "./dex-sdk";
import { useWriteDexSdk } from "./write-dex-sdk-context";

const DEFAULT_DURATION = 8_000;

export interface ClientNotification {
  id?: string;
  type: NotificationType | ClientNotificationType;
  amount?: number;
  price?: number;
  side?: OrderSide;
  size?: number;
  market?: Market;
}

type Order = NonNullable<UserNotification["notification"]["order"]>;
type TWAP = NonNullable<UserNotification["notification"]["twap"]>;
type NotificationMetadata = NonNullable<UserNotification["notification"]["notification_metadata"]>;

const CUSTOM_ID_TYPES = [
  NotificationType.AccountDeposit,
  NotificationType.AccountWithdrawal,
  NotificationType.TpSlSet,
  NotificationType.TpHit,
  NotificationType.SlHit,
  NotificationType.TpCancelled,
  NotificationType.SlCancelled,
  ClientNotificationType.OrderCancellationPlaced,
  ClientNotificationType.PositionCancellationPlaced,
  ClientNotificationType.PositionReversalPlaced,
  ClientNotificationType.TwapCancellationPlaced,
  ClientNotificationType.OrderCancellationErrored,
  ClientNotificationType.PositionCancellationErrored,
  ClientNotificationType.PositionReversalErrored,
  ClientNotificationType.TwapCancellationErrored,
];

interface ToastData {
  type: NotificationType | ClientNotificationType;
  market?: Market;
  order?: Partial<Order>;
  metadata?: Partial<NotificationMetadata>;
  twap?: Partial<TWAP>;
  t: ReturnType<typeof useTranslations<"toast">>;
  tGeneral: ReturnType<typeof useTranslations<"general">>;
  prevToast?: VisibleToast;
}

const KNOWN_CANCELLATION_REASONS = new Set([
  "PostOnlyViolation",
  "IOCViolation",
  "ReduceOnlyViolation",
  "PlaceMakerOrderViolation",
  "DuplicateClientOrderIdViolation",
  "MaxFillLimitViolation",
  "PositionUpdateViolation",
  "ClearinghouseSettleViolation",
  "ClearinghouseStoppedMatching",
  "DisallowedSelfTrading",
  "OrderPreCancelled",
  "DeadMansSwitchExpired",
  "OrderCancelledBySystem",
  "OrderCancelledBySystemDueToError",
]);

const getIsPartialFill = (
  type: NotificationType | ClientNotificationType,
  order?: Partial<Order>,
) => {
  if (usesRemovedSizeDelta(type)) {
    const filled = getFilledSize(type, order);
    return filled !== null && filled > 0 && filled < (order?.orig_size ?? 0);
  }

  return Boolean(order?.size_delta && order.size_delta !== 0);
};

export const buildToastData = ({
  type,
  market,
  order,
  metadata,
  twap,
  t,
  tGeneral,
  prevToast,
}: ToastData): CreateToastArgs | undefined => {
  const marketName = market?.market_name;
  const tokenName = marketName ? getMarketCurrencies(marketName).primaryCurrency : "";
  const originalSize = order?.orig_size || twap?.orig_size || 0;
  const side =
    (order?.is_buy ?? twap?.is_buy ?? originalSize > 0) ? tGeneral("long") : tGeneral("short");
  const remainingSize = order?.remaining_size ?? twap?.remaining_size ?? 0;
  const orderPrice = order?.price;
  const failureReason = metadata?.reason;
  const isPartialFill = getIsPartialFill(type, order);
  const cancelledSize = getCancelledSize(type, order, twap) ?? 0;
  const filledSize = getFilledSize(type, order) ?? 0;
  const amount = metadata?.amount;

  let title = "";
  let summary = "";
  const events: Array<string | (() => ReactNode)> = [];
  let variant: CreateToastArgs["variant"] = "success";

  const formatSize = (size: number) => {
    if (!market) {
      throw new Error("Market is required");
    }
    return NumberFormatter.size(Math.abs(size), { szDecimals: market.sz_decimals });
  };
  const formatPrice = (price: number) => {
    if (!market) {
      throw new Error("Market is required");
    }
    return NumberFormatter.price(price, { pxDecimals: market.px_decimals });
  };

  const buildTokenSummary = (key: string) => {
    return t(key, { tokenName });
  };

  const buildMarketSummary = (key: string) => {
    if (!marketName) {
      throw new Error("Market name is required");
    }
    return t(key, { marketName });
  };

  const buildCancelledEvent = (key: string) => {
    if (!marketName) {
      throw new Error("Market name is required");
    }
    return t(key, {
      size: formatSize(cancelledSize),
      marketName,
    });
  };

  const buildTransferEvent = (key: string) => {
    if (!amount) {
      throw new Error("Amount is required");
    }
    return t(key, {
      amount: NumberFormatter.usdc(amount),
    });
  };

  const buildOrderEvent = (key: string, size: number = originalSize) => {
    if (!marketName) {
      throw new Error("Market name is required");
    }
    return t(key, {
      ...(orderPrice ? { price: formatPrice(orderPrice) } : {}),
      side,
      size: formatSize(size),
      marketName,
    });
  };

  const buildCancellationEvent = (key: string) => {
    if (!marketName) {
      throw new Error("Market name is required");
    }
    return t(key, {
      side,
      size: formatSize(originalSize),
      marketName,
    });
  };

  const buildTpSlEvent = (key: string) => {
    if (!marketName) {
      throw new Error("Market name is required");
    }
    return t(key, {
      marketName,
    });
  };

  const pushReasonEvent = (reason: string | undefined) => {
    if (!reason || reason === "OrderCancelledByUser") return;
    const label = KNOWN_CANCELLATION_REASONS.has(reason)
      ? t(`events.cancellationReasons.${reason}`)
      : reason;
    events.push(t("events.cancellationReason", { reason: label }));
  };

  try {
    switch (type) {
      case NotificationType.MarketOrderPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildTokenSummary("summaries.orderPlacedSuccess");
        events.push(buildOrderEvent("events.submittedMarketOrder"));
        break;
      }
      case ClientNotificationType.LimitOrderSubmitted: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildTokenSummary("summaries.orderSubmittedSuccess");
        break;
      }
      case NotificationType.LimitOrderPlaced: {
        variant = "success";
        title = t("titles.success");
        summary = buildTokenSummary("summaries.orderPlacedSuccess");
        events.push(buildOrderEvent("events.placedLimitOrder"));
        break;
      }
      case NotificationType.StopMarketOrderPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildTokenSummary("summaries.orderPlacedSuccess");
        events.push(buildOrderEvent("events.submittedStopMarketOrder"));
        break;
      }
      case NotificationType.StopLimitOrderPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildTokenSummary("summaries.orderPlacedSuccess");
        events.push(buildOrderEvent("events.submittedStopLimitOrder"));
        break;
      }
      case ClientNotificationType.OrderCancellationPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildMarketSummary("summaries.orderCancellationPlaced");
        events.push(buildCancellationEvent("events.submittedOrderCancellation"));
        break;
      }
      case ClientNotificationType.PositionCancellationPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildMarketSummary("summaries.positionCancellationPlaced");
        events.push(buildCancellationEvent("events.submittedPositionCancellation"));
        break;
      }
      case ClientNotificationType.PositionReversalPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildMarketSummary("summaries.positionReversalPlaced");
        events.push(buildCancellationEvent("events.submittedPositionReversal"));
        break;
      }
      case ClientNotificationType.TwapCancellationPlaced: {
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = buildMarketSummary("summaries.twapCancellationPlaced");
        events.push(buildCancellationEvent("events.submittedTwapCancellation"));
        break;
      }
      case ClientNotificationType.OrderCancellationErrored: {
        variant = "error";
        title = t("titles.failure");
        summary = buildTokenSummary("summaries.orderCancellationError");
        events.push(buildCancellationEvent("events.orderCancellationError"));
        break;
      }
      case ClientNotificationType.PositionCancellationErrored: {
        variant = "error";
        title = t("titles.failure");
        summary = buildTokenSummary("summaries.positionCancellationError");
        events.push(buildCancellationEvent("events.positionCancellationError"));
        break;
      }
      case ClientNotificationType.PositionReversalErrored: {
        variant = "error";
        title = t("titles.failure");
        summary = buildTokenSummary("summaries.positionReversalError");
        events.push(buildCancellationEvent("events.positionReversalError"));
        break;
      }
      case ClientNotificationType.TwapCancellationErrored: {
        variant = "error";
        title = t("titles.failure");
        summary = buildTokenSummary("summaries.twapCancelledError");
        events.push(buildCancellationEvent("events.twapCancellationError"));
        break;
      }
      case NotificationType.StopMarketOrderTriggered: {
        variant = "success";
        title = t("titles.success");
        summary = buildTokenSummary("summaries.stopMarketTriggeredSuccess");
        events.push(buildOrderEvent("events.triggeredStopMarketOrder"));
        break;
      }
      case NotificationType.StopLimitOrderTriggered: {
        variant = "success";
        title = t("titles.success");
        summary = buildTokenSummary("summaries.stopLimitTriggeredSuccess");
        events.push(buildOrderEvent("events.triggeredStopLimitOrder"));
        break;
      }
      case NotificationType.OrderPartiallyFilled: {
        // Skip partial fills for now (only showing end-states)
        return;
      }
      case NotificationType.OrderFilled: {
        variant = "success";
        title = t("titles.success");
        summary = buildTokenSummary("summaries.orderFilledSuccess");
        events.push(buildOrderEvent("events.filledOrder"));
        break;
      }
      case NotificationType.OrderSizeReduced: {
        // Skip size reductions for now (only showing end-states)
        return;
      }
      case NotificationType.OrderCancelled: {
        if (!marketName) {
          throw new Error("Market name is required");
        }

        variant = "success";
        title = t("titles.success");
        summary = buildTokenSummary("summaries.orderCancelledSuccess");
        if (isPartialFill) {
          events.push(buildOrderEvent("events.filledOrder", filledSize));
        }
        events.push(
          t("events.cancelledOrder", {
            size: formatSize(cancelledSize),
            marketName,
          }),
        );
        pushReasonEvent(order?.cancellation_reason);
        Logger.warn("Order Cancelled", {
          marketName,
          size: formatSize(originalSize),
          side,
          reason: order?.cancellation_reason,
        });
        break;
      }
      case NotificationType.OrderRejected: {
        if (!marketName) {
          throw new Error("Market name is required");
        }
        variant = "error";
        title = t("titles.failure");
        summary = buildTokenSummary("summaries.orderCancelledError");
        events.push(buildCancelledEvent("events.cancelledOrder"));
        pushReasonEvent(failureReason);
        Logger.warn("An order was rejected", {
          marketName,
          size: formatSize(originalSize),
          side,
          reason: failureReason,
        });
        break;
      }
      case NotificationType.OrderErrored: {
        // Remove this once fixed on BACKEND , when cancellation is successful, the order errored notification is not sent
        if (prevToast?.meta.metadata?.type === ClientNotificationType.OrderCancellationPlaced) {
          if (!marketName) {
            throw new Error("Market name is required");
          }
          variant = "success";
          title = t("titles.success");
          summary = buildTokenSummary("summaries.orderCancelledSuccess");
          if (isPartialFill) {
            events.push(buildOrderEvent("events.filledOrder", filledSize));
          }
          events.push(
            t("events.cancelledOrder", {
              size: formatSize(cancelledSize),
              marketName,
            }),
          );
          break;
        }

        variant = "error";
        title = isPartialFill ? t("titles.alert") : t("titles.failure");
        summary = buildTokenSummary("summaries.orderCancelledError");
        if (isPartialFill) {
          events.push(buildOrderEvent("events.filledOrder", filledSize));
        }
        events.push(buildCancelledEvent("events.cancelledOrder"));
        pushReasonEvent(failureReason);
        break;
      }
      case NotificationType.TwapOrderPlaced: {
        if (!marketName) {
          throw new Error("Market name is required");
        }
        variant = "loading";
        title = t("titles.orderProcessing");
        summary = t("summaries.twapPlacedSuccess");
        events.push(
          t("events.submittedTwapOrder", {
            side,
            size: formatSize(originalSize),
            marketName,
          }),
        );
        break;
      }
      case ClientNotificationType.TwapPlaced: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.twapPlacedSuccess");
        events.push(t("events.twapPlacedSuccess"));
        break;
      }
      case NotificationType.TwapOrderTriggered: {
        if (!marketName) {
          throw new Error("Market name and remaining size are required");
        }
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.twapFilledSuccess");
        if (remainingSize) {
          events.push(
            t("events.remainingOrder", {
              size: formatSize(remainingSize),
              marketName,
            }),
          );
          events.push(t("events.seeTwapTabProgress"));
        } else {
          events.push(buildOrderEvent("events.twapCompletedSuccess"));
          events.push(t("events.seeTwapHistory"));
        }
        break;
      }
      case NotificationType.TwapOrderCompleted: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.twapFilledSuccess");
        events.push(buildOrderEvent("events.filledOrder"));
        break;
      }
      case NotificationType.TwapOrderCancelled: {
        variant = "success";
        title = t("titles.success");
        if (isPartialFill) {
          summary = t("summaries.twapCancelledPartiallyFilled", {
            tokenName,
          });
          events.push(buildOrderEvent("events.filledOrder"));
        } else {
          summary = t("summaries.twapCancelledSuccess");
        }
        events.push(buildCancelledEvent("events.cancelledOrder"));
        events.push(t("events.seeTwapHistory"));
        break;
      }
      case NotificationType.TwapOrderErrored: {
        variant = isPartialFill ? "warning" : "error";
        title = t("titles.twapAlert");
        if (isPartialFill) {
          summary = t("summaries.twapCancelledPartiallyFilled", {
            tokenName,
          });
          events.push(buildOrderEvent("events.filledOrder"));
        } else {
          summary = t("summaries.twapCancelledError");
        }
        events.push(buildCancelledEvent("events.cancelledOrder"));
        pushReasonEvent(failureReason);
        break;
      }
      case NotificationType.AccountDeposit: {
        if (!amount) {
          throw new Error("Amount is required");
        }
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.depositSuccess");
        events.push(buildTransferEvent("events.depositAmountSuccess"));
        break;
      }
      case NotificationType.AccountWithdrawal: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.withdrawSuccess");
        events.push(buildTransferEvent("events.withdrawAmountSuccess"));
        break;
      }
      case NotificationType.TpSlSet: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.tpSlSetSuccess");
        events.push(buildTpSlEvent("events.tpSlSetSuccess"));
        break;
      }
      case NotificationType.TpHit: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.tpHitSuccess");
        events.push(buildTpSlEvent("events.tpHitSuccess"));
        break;
      }
      case NotificationType.SlHit: {
        variant = "warning";
        title = t("titles.alert");
        summary = t("summaries.slHitSuccess");
        events.push(buildTpSlEvent("events.slHitSuccess"));
        break;
      }
      case NotificationType.TpCancelled: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.tpCancelledSuccess");
        events.push(buildTpSlEvent("events.tpCancelledSuccess"));
        break;
      }
      case NotificationType.SlCancelled: {
        variant = "success";
        title = t("titles.success");
        summary = t("summaries.slCancelledSuccess");
        events.push(buildTpSlEvent("events.slCancelledSuccess"));
        break;
      }
      default: {
        throw new Error(`Unsupported notification type: ${type}`);
      }
    }
  } catch (error) {
    Logger.error(error, {
      message: "Failed to get toast data",
      type,
    });
  }

  const description = () => (
    <div className={stack({ gap: "4" })}>
      <span className={css({ color: "text.secondary" })}>{summary}</span>
      {events.map((event, i) => (
        <span
          key={typeof event === "string" ? event : i}
          className={css({ color: "text.tertiary" })}
        >
          {typeof event === "string" ? event : event()}
        </span>
      ))}
    </div>
  );

  return { title, description, variant };
};

const buildToastId = ({
  id,
  type,
  marketName,
  amount,
}: {
  id?: string;
  type: NotificationType | ClientNotificationType;
  marketName?: string;
  amount?: number;
}) => {
  if (id) {
    return id;
  }
  if (CUSTOM_ID_TYPES.includes(type)) {
    return [type, marketName, amount ? NumberFormatter.usdc(amount) : undefined]
      .filter(Boolean)
      .join("-");
  }
  return uuidv4();
};

interface NotificationContextType {
  avoidNotification: (notificationType: NotificationType, forTime?: number) => () => void;
  clientNotification: (notification: ClientNotification) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const { subaccountAddress } = useWriteDexSdk();
  const t = useTranslations("toast");
  const tGeneral = useTranslations("general");
  const queryClient = useQueryClient();
  const avoidNotifications = useRef<
    Map<NotificationType | ClientNotificationType, Map<symbol, { avoidUntil: number }>>
  >(new Map());

  const shouldAvoidNotification = useCallback(
    (notificationType: NotificationType | ClientNotificationType) => {
      const entries = avoidNotifications.current.get(notificationType);
      if (!entries) return false;

      const now = Date.now();
      for (const entry of entries.values()) {
        if (entry.avoidUntil > now) return true;
      }
      return false;
    },
    [avoidNotifications],
  );

  const avoidNotification = useCallback(
    (notificationType: NotificationType | ClientNotificationType, forTime?: number) => {
      const id = Symbol();
      let entries = avoidNotifications.current.get(notificationType);
      if (!entries) {
        entries = new Map();
        avoidNotifications.current.set(notificationType, entries);
      }
      entries.set(id, { avoidUntil: Date.now() + (forTime ?? 0) });

      return () => {
        entries.delete(id);
        if (entries.size === 0) {
          avoidNotifications.current.delete(notificationType);
        }
      };
    },
    [avoidNotifications],
  );

  const clientNotification = useCallback(
    (notification: ClientNotification) => {
      if (shouldAvoidNotification(notification.type)) return;

      const { id, type, amount, side, size, market, price } = notification;

      const toastId = buildToastId({ id, type, amount });

      const order: Partial<Order> = {
        orig_size: size,
        is_buy: side === "buyLong",
        price: price ?? 0,
      };

      const toastData = buildToastData({
        type,
        market,
        order,
        metadata: {
          amount,
        },
        t,
        tGeneral,
      });

      if (!toastData) {
        return;
      }

      toast({
        ...toastData,
        id: toastId,
        duration: DEFAULT_DURATION,
        metadata: { type },
      });
    },
    [t, tGeneral, shouldAvoidNotification],
  );

  const serverNotification = useEffectEvent(
    async (notification: UserNotification["notification"]) => {
      if (shouldAvoidNotification(notification.notification_type)) return;

      const {
        order,
        twap,
        notification_metadata: metadata,
        notification_type: type,
      } = notification;

      const marketAddr = order?.market || twap?.market;
      const market = marketAddr ? await getMarketByAddress(queryClient, marketAddr) : undefined;
      const marketName = market?.market_name ?? "";
      const orderId = order?.order_id || twap?.order_id;
      const clientOrderId = order?.client_order_id || twap?.client_order_id; // TODO: include twap.client_order_id once available

      const toastId = buildToastId({
        id: clientOrderId || orderId,
        marketName,
        type,
        amount: metadata?.amount,
      });

      const visibleToasts = toast.getVisibleToasts();

      const prevToast = visibleToasts.find((visibleToast) => visibleToast.id === toastId);

      const toastData = buildToastData({
        type,
        order,
        twap,
        metadata,
        market,
        t,
        tGeneral,
        prevToast,
      });

      if (!toastData) {
        return;
      }

      if (prevToast?.id) {
        toast.update(prevToast.id, toastData);
      } else {
        toast({
          ...toastData,
          id: toastId,
          duration: DEFAULT_DURATION,
        });
      }
    },
  );

  useEffect(() => {
    if (!subaccountAddress) return;

    // Subscribe to user notifications
    const unsubscribe = readDexSdk.userNotifications.subscribeByAddr(subaccountAddress, (data) => {
      useAgentObservedFills.getState().record(subaccountAddress, data.notification);
      void serverNotification(data.notification);
    });

    return unsubscribe;
  }, [subaccountAddress]);

  const value = useMemo(
    () => ({ avoidNotification, clientNotification }),
    [avoidNotification, clientNotification],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
